import { useState } from 'react'
// Removed unused imports to fix compilation
import PromptInput from './components/PromptInput'
import FilterPanel from './components/FilterPanel'
import GeneratedImage from './components/GeneratedImage'
import GenerationHistory from './components/Generation/GenerationHistory'
import AuthModal from './components/Auth/AuthModal'
import UserProfile from './components/Profile/UserProfile'
import ImageHistory from './components/History/ImageHistory'
import SavedFeetManager from './components/History/SavedFeetManager'
import DiscoverFeed from './components/Discover/DiscoverFeed'
import DiscoverPanel from './components/Discover/DiscoverPanel'
import GiftModal from './components/Gift/GiftModal'
import GiftRedemption from './components/Gift/GiftRedemption'
import GlobalSearch from './components/Search/GlobalSearch'
import ResponsiveLayout from './components/Layout/ResponsiveLayout'
import { useAuth } from './context/AuthContext'
import { useDeviceType } from './hooks/useDeviceType'
import { useGenerationLimit } from './hooks/useGenerationLimit'
import { UserSearchResult, ImageSearchResult } from './services/globalSearch'
import { generateFeetImage, generateMockImage } from './services/openai'
import { saveImage, isDemoModeFlag } from './lib/supabase'
import { FilterOptions, GenerationResult } from './types'

function App() {
  const { user, loading } = useAuth()
  const { isMobile, isDesktop } = useDeviceType()
  
  const {
    canGenerate,
    remainingGenerations,
    incrementAnonymousUsage,
    incrementMonthlyUsage,
    consumeCredits,
    getGenerationType,
    getGenerationCost,
    isAtLimit
  } = useGenerationLimit()
  
  const [prompt, setPrompt] = useState('')
  const [filters, setFilters] = useState<FilterOptions>({
    background: [],
    nailColor: [],
    jewelry: [],
    shoes: [],
    style: [],
    nailLength: [],
    season: [],
    skinColor: [],
    angle: [],
    foodCoverage: [],
    fantasyTransformation: [],
    glamorEffects: [],
    commonFilters: [],
    photoEffects: [],
    furTexture: [],
    surfaceTexture: [],
    bondage: [],
    occupation: [],
    toeHat: [],
    paraphernalia: []
  })
  const [generatedImage, setGeneratedImage] = useState<string | null>(null)
  const [imageMetadata, setImageMetadata] = useState<{
    model?: string
    generatedAt?: string
    footPersona?: any
  } | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationStatus, setGenerationStatus] = useState('')
  
  // Navigation state - Create is the default active tab
  const [activeTab, setActiveTab] = useState<'create' | 'history' | 'savedFeet' | 'discover' | null>('create')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [showGift, setShowGift] = useState(false)
  const [showRedemption, setShowRedemption] = useState(false)
  const [showGlobalSearch, setShowGlobalSearch] = useState(false)
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin')
  const [showFilters, setShowFilters] = useState(false)
  const [newGenerations, setNewGenerations] = useState<any[]>([])
  const [uploadedImages, setUploadedImages] = useState<File[]>([])

  // Navigation handlers
  const handleShowCreate = () => {
    setActiveTab('create')
  }

  const handleShowHistory = () => {
    setActiveTab('history')
  }

  const handleShowSavedFeet = () => {
    setActiveTab('savedFeet')
  }

  const handleShowDiscover = () => {
    setActiveTab('discover')
  }

  const handleCloseCurrentTab = () => {
    setActiveTab('create')
  }

  // Global search handlers
  const handleShowGlobalSearch = () => {
    setShowGlobalSearch(true)
  }

  const handleUserSelect = (user: UserSearchResult) => {
    console.log('Selected user:', user)
    setShowGlobalSearch(false)
  }

  const handleTagSelect = (tag: string, category: string) => {
    console.log('Selected tag:', tag, 'in category:', category)
    
    const categoryMapping: Record<string, keyof FilterOptions> = {
      'background': 'background',
      'nailColor': 'nailColor',
      'jewelry': 'jewelry',
      'shoes': 'shoes',
      'style': 'style',
      'nailLength': 'nailLength',
      'season': 'season'
    }
    
    const filterKey = categoryMapping[category]
    if (filterKey) {
      setFilters(prev => ({
        ...prev,
        [filterKey]: [tag]
      }))
    }
    
    setActiveTab('create')
    setShowGlobalSearch(false)
  }

  const handleImageSelect = (image: ImageSearchResult) => {
    console.log('Selected image:', image)
    setShowGlobalSearch(false)
  }

  // Show full-screen loading during auth restoration
  if (loading) {
    console.log('App: Loading state is true, showing loading screen')
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-white">🦶</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Toefetti</h1>
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            <span className="ml-2">Loading...</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">Restoring your session</p>
          {/* Debug info */}
          <div className="mt-4 text-xs text-gray-400">
            <p>Auth loading: {loading ? 'true' : 'false'}</p>
            <p>User: {user ? user.email : 'null'}</p>
            <p>Demo mode: {isDemoModeFlag ? 'true' : 'false'}</p>
            <p>Time: {new Date().toLocaleTimeString()}</p>
          </div>
        </div>
      </div>
    )
  }

  console.log('App: Loading complete, showing main app')

  const handleFilterChange = (filterType: keyof FilterOptions, value: string) => {
    setFilters(prev => {
      const currentValues = prev[filterType] as string[]
      const isSelected = currentValues.includes(value)
      
      if (value === '') {
        // Clear all selections if empty value is selected
        return {
          ...prev,
          [filterType]: []
        }
      }
      
      if (isSelected) {
        // Remove the value if already selected
        return {
          ...prev,
          [filterType]: currentValues.filter(v => v !== value)
        }
      } else {
        // Add the value to the selection
        return {
          ...prev,
          [filterType]: [...currentValues, value]
        }
      }
    })
  }

  const hasActiveFilters = () => {
    return Object.values(filters).some(filterArray => filterArray.length > 0)
  }

  const handleGenerate = async () => {
    // Allow generation if there's a prompt OR active filters OR uploaded images
    if (!prompt.trim() && !hasActiveFilters() && uploadedImages.length === 0) {
      return
    }
    
    // Check generation limits
    if (!canGenerate) {
      if (!user) {
        setAuthMode('signup')
        setShowAuthModal(true)
        return
      } else {
        // User needs credits or subscription
        setGenerationStatus('Insufficient credits or subscription required')
        setTimeout(() => setGenerationStatus(''), 3000)
        return
      }
    }
    
    setIsGenerating(true)
    setGenerationStatus('Starting image generation...')
    
    try {
      const generationType = getGenerationType()
      const cost = getGenerationCost()
      
      // Handle credit consumption for non-subscription users
      if (generationType === 'credits' && cost > 0) {
        setGenerationStatus('Consuming credits...')
        const creditConsumed = await consumeCredits(cost)
        if (!creditConsumed) {
          setGenerationStatus('Failed to consume credits')
          setTimeout(() => setGenerationStatus(''), 3000)
          return
        }
      }
      
      // Add overall timeout to prevent infinite hanging
      const generatePromise = generateFeetImage({ prompt, filters, uploadedImages })
      const timeoutPromise = new Promise<GenerationResult>((_, reject) => 
        setTimeout(() => reject(new Error('Generation timeout - taking too long')), 45000) // 45 second timeout
      )
      
      setGenerationStatus('Connecting to AI service...')
      const result = await Promise.race([generatePromise, timeoutPromise])
      
      setGenerationStatus('Processing generated image...')
      setGeneratedImage(result.imageUrl)
      setImageMetadata({
        model: result.model,
        generatedAt: new Date().toISOString()
      })
      
      // Increment usage count for anonymous users only
      if (generationType === 'anonymous') {
        incrementAnonymousUsage()
      }
      
      // Increment monthly usage count for logged-in users
      if (user) {
        incrementMonthlyUsage()
      }
      
      // Create the image object for display
      const imageForDisplay = {
        id: Date.now().toString(),
        title: `Generated on ${new Date().toLocaleDateString()}`,
        description: prompt || (uploadedImages.length > 0 ? 'Generated with reference images' : 'Generated with filters only'),
        prompt_text: prompt,
        image_url: result.imageUrl,
        filters_used: filters,
        generation_settings: {
          timestamp: new Date().toISOString(),
          model: result.model,
          hasReferenceImages: uploadedImages.length > 0,
          generationType,
          cost
        },
        created_at: new Date().toISOString(),
        is_public: false,
        likes_count: 0,
        views_count: 0,
        download_count: 0
      }
      
      // Always add to desktop view immediately
      if (isDesktop) {
        setNewGenerations(prev => [imageForDisplay, ...prev])
      }
      
      setGenerationStatus('Saving to your account...')
      // Save image to database if user is logged in
      if (user && result.imageUrl) {
        try {
          // Add timeout to saveImage call
          const savePromise = saveImage({
            user_id: user.id,
            title: `Generated on ${new Date().toLocaleDateString()}`,
            description: prompt || (uploadedImages.length > 0 ? 'Generated with reference images' : 'Generated with filters only'),
            prompt_text: prompt,
            image_url: result.imageUrl,
            filters_used: filters,
            voice_persona: {},
            generation_settings: {
              timestamp: new Date().toISOString(),
              model: result.model,
              hasReferenceImages: uploadedImages.length > 0,
              generationType,
              cost
            },
            is_public: false,
            is_nsfw: false,
            likes_count: 0,
            views_count: 0,
            download_count: 0
          }, uploadedImages)
          
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Image save timeout')), 10000) // 10 second timeout
          )
          
          const savedImage = await Promise.race([savePromise, timeoutPromise])
          setGenerationStatus('Image saved successfully!')
          
          // Update the image with the real database ID if save succeeded
          if (savedImage && isDesktop) {
            setNewGenerations(prev => prev.map(img => 
              img.id === imageForDisplay.id 
                ? { ...img, id: savedImage.id || img.id }
                : img
            ))
          }
        } catch (saveError) {
          console.error('Error saving image to database:', saveError)
          if (saveError instanceof Error && saveError.message.includes('timeout')) {
            setGenerationStatus('Image generated (save timed out)')
          } else {
            setGenerationStatus('Image generated (save failed)')
          }
          // Continue anyway - image generation succeeded and is already displayed
        }
      } else {
        setGenerationStatus('Image generated successfully!')
      }
      
      // Clear uploaded images after successful generation
      setUploadedImages([])
      
      // Show signup prompt if anonymous user just used their last free generation
      if (generationType === 'anonymous' && isAtLimit) {
        setTimeout(() => {
          setAuthMode('signup')
          setShowAuthModal(true)
        }, 2000) // Show modal after 2 seconds
      }
      
    } catch (error) {
      console.error('Error generating image:', error)
      setGenerationStatus('Generation failed, trying fallback...')
      
      // If we consumed credits but generation failed, we should ideally refund them
      // For now, we'll let the user know about the issue
      
      // Fallback to mock generation as last resort
      try {
        const mockImageUrl = await generateMockImage({ prompt, filters, uploadedImages })
        setGeneratedImage(mockImageUrl)
        setImageMetadata({
          model: 'mock-generation-fallback',
          generatedAt: new Date().toISOString()
        })
        
        // Still increment usage for anonymous users
        if (getGenerationType() === 'anonymous') {
          incrementAnonymousUsage()
        }
        
        // Add mock image to desktop view
        if (isDesktop) {
          const mockImageForDisplay = {
            id: Date.now().toString(),
            title: `Generated on ${new Date().toLocaleDateString()}`,
            description: prompt || (uploadedImages.length > 0 ? 'Generated with reference images' : 'Generated with filters only'),
            prompt_text: prompt,
            image_url: mockImageUrl,
            filters_used: filters,
            generation_settings: {
              timestamp: new Date().toISOString(),
              model: 'mock-generation-fallback',
              hasReferenceImages: uploadedImages.length > 0,
              generationType: getGenerationType(),
              cost: 0 // Fallback is free
            },
            created_at: new Date().toISOString(),
            is_public: false,
            likes_count: 0,
            views_count: 0,
            download_count: 0
          }
          setNewGenerations(prev => [mockImageForDisplay, ...prev])
        }
        
        // Clear uploaded images after fallback generation too
        setUploadedImages([])
        setGenerationStatus('Using fallback generation')
      } catch (mockError) {
        console.error('Mock generation also failed:', mockError)
        setGenerationStatus('Generation failed completely')
      }
    } finally {
      setIsGenerating(false)
      // Clear status after a delay
      setTimeout(() => setGenerationStatus(''), 3000)
    }
  }

  // Content that goes inside the layout
  const getMainContent = () => {
    // For mobile, always show the traditional interface regardless of tab
    if (isMobile) {
      return (
        <div className="space-y-6 max-w-none">
          {/* Prompt Input */}
          <div className="space-y-4">
            <PromptInput
              value={prompt}
              onChange={setPrompt}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              disabled={!user && !canGenerate}
              remainingGenerations={remainingGenerations}
              uploadedImages={uploadedImages}
              onImagesChange={setUploadedImages}
            />
          </div>

          {/* Filters */}
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
          />

          {/* Generated Image */}
          {generatedImage && (
            <GeneratedImage
              imageUrl={generatedImage}
              prompt={prompt}
              filters={filters}
              model={imageMetadata?.model}
              generatedAt={imageMetadata?.generatedAt}
              footPersona={imageMetadata?.footPersona}
            />
          )}

          {/* Loading State */}
          {isGenerating && (
            <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
                <p className="text-gray-600 font-medium">{generationStatus}</p>
                <p className="text-sm text-gray-500 text-center">
                  This may take a few moments while our AI creates the perfect image
                </p>
              </div>
            </div>
          )}
        </div>
      )
    }

    // Desktop: Show different content based on active tab
    switch (activeTab) {
      case 'create':
        return (
          <GenerationHistory 
            newGenerations={newGenerations} 
            isGenerating={isGenerating}
            generationStatus={generationStatus}
          />
        )
      case 'history':
        return (
          <ImageHistory
            isOpen={true}
            onClose={handleCloseCurrentTab}
            newGenerations={newGenerations}
            inline={true}
          />
        )
      case 'savedFeet':
        return (
          <SavedFeetManager
            isOpen={true}
            onClose={handleCloseCurrentTab}
          />
        )
      case 'discover':
        // For desktop, show empty content in main area since Discover is in right panel
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Discover Amazing Creations</h2>
              <p className="text-gray-600 mb-4">
                Explore public images from the community and create your own inspired by what you see.
              </p>
              <p className="text-sm text-gray-500">
                Use the creation tools in the right panel to generate new images.
              </p>
            </div>
          </div>
        )
      default:
        return (
          <GenerationHistory 
            newGenerations={newGenerations} 
            isGenerating={isGenerating}
            generationStatus={generationStatus}
          />
        )
    }
  }

  const mainContent = getMainContent()

  // Filter section for desktop
  const filterSection = (
    <FilterPanel
      filters={filters}
      onFilterChange={handleFilterChange}
    />
  )

  // Discover section for desktop
  const discoverSection = (
    <DiscoverPanel
      prompt={prompt}
      onPromptChange={setPrompt}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      filters={filters}
      onFilterChange={handleFilterChange}
      showFilters={showFilters}
      onToggleFilters={() => setShowFilters(prev => !prev)}
      canGenerate={canGenerate}
      uploadedImages={uploadedImages}
      onImagesChange={setUploadedImages}
      onSwitchToCreate={handleShowCreate}
    />
  )

  return (
    <div className="App">
      <ResponsiveLayout
        onShowAuthModal={() => setShowAuthModal(true)}
        onShowProfile={() => setShowProfile(true)}
        onShowCreate={handleShowCreate}
        onShowHistory={handleShowHistory}
        onShowSavedFeet={handleShowSavedFeet}
        onShowDiscover={handleShowDiscover}
        onShowGift={() => setShowGift(true)}
        onShowRedemption={() => setShowRedemption(true)}
        onShowGlobalSearch={handleShowGlobalSearch}
        activeTab={activeTab}
        authMode={authMode}
        setAuthMode={setAuthMode}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(prev => !prev)}
        prompt={prompt}
        onPromptChange={setPrompt}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
        filterSection={filterSection}
        discoverSection={discoverSection}
        canGenerate={canGenerate}
        remainingGenerations={remainingGenerations}
        uploadedImages={uploadedImages}
        onImagesChange={setUploadedImages}
      >
        {mainContent}
      </ResponsiveLayout>

      {/* Modals */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode={authMode}
        />
      )}

      {showProfile && (
        <UserProfile
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
        />
      )}

      {/* Mobile Discover Modal */}
      {isMobile && activeTab === 'discover' && (
        <DiscoverFeed
          isOpen={true}
          onClose={handleCloseCurrentTab}
        />
      )}

      {showGift && (
        <GiftModal
          isOpen={showGift}
          onClose={() => setShowGift(false)}
        />
      )}

      {showRedemption && (
        <GiftRedemption
          isOpen={showRedemption}
          onClose={() => setShowRedemption(false)}
          onRedemptionSuccess={() => {
            // Optionally refresh user data or show success message
            console.log('Gift redeemed successfully!')
          }}
        />
      )}

      {showGlobalSearch && (
        <GlobalSearch
          isOpen={showGlobalSearch}
          onClose={() => setShowGlobalSearch(false)}
          onUserSelect={handleUserSelect}
          onTagSelect={handleTagSelect}
          onImageSelect={handleImageSelect}
        />
      )}
    </div>
  )
}

export default App 